import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Store from './Store';
import AppShell from './components/layout/AppShell';
import MyRoute from './components/routing/MyRoute';
import SetAuthToken from './components/util/SetAuthToken';
import { loadUser } from './action/Auth';
import { getStatistics } from './action/Statistics';
import Alert from './components/layout/Alert';
import { SocketContext, socket } from './components/context/socket';
import { ThemeProvider } from './components/context/ThemeContext';
import Toaster from '@/components/ui/toaster';
import './App.css';

const Admin = lazy(() => import('./components/Admin'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Login = lazy(() => import('./components/auth/Login'));
const Main = lazy(() => import('./components/Main'));
const Orders = lazy(() => import('./components/Orders'));
const PrintOrder = lazy(() => import('./components/PrintOrder'));
const Products = lazy(() => import('./components/Products'));
const Register = lazy(() => import('./components/auth/Register'));
const Setting = lazy(() => import('./components/Setting'));
const ChatPage = lazy(() => import('./components/chat/ChatPage'));

if (localStorage.token) {
  SetAuthToken(localStorage.token);
}

const App = () => {
  useEffect(() => {
    if (localStorage.token && !Store.getState().AuthRedux.user) {
      Store.dispatch(loadUser());
    }
    if (localStorage.user) {
      const user = JSON.parse(localStorage.user);
      if (user && user.partition) Store.dispatch(getStatistics());
    }
  }, []);

  useEffect(() => {
    // Prevent iOS Safari bottom blank gap bug on keyboard dismiss
    const handleFocusOut = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 50);
      }
    };
    document.addEventListener('focusout', handleFocusOut);
    return () => document.removeEventListener('focusout', handleFocusOut);
  }, []);

  const showContent = () => {
    if (window.location.pathname === '/printorders') {
      return (
        <Suspense fallback={null}>
          <PrintOrder />
        </Suspense>
      );
    }

    if (window.location.pathname === '/privacy') {
      window.location.href = '/privacy.html';
      return null;
    }

    return (
      <AppShell>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<MyRoute component={Main} />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <MyRoute needShop={true} isRestricted={true} component={Dashboard} />
              }
            />
            <Route
              path="/products"
              element={
                <MyRoute isRestricted={true} needShop={true} component={Products} />
              }
            />
            <Route
              path="/orders"
              element={
                <MyRoute isRestricted={true} needShop={true} component={Orders} />
              }
            />
            <Route
              path="/admin"
              element={<MyRoute isRestricted={true} component={Admin} />}
            />
            <Route path="/setting" element={<MyRoute component={Setting} />} />
            <Route
              path="/chat"
              element={
                <MyRoute isRestricted={true} needShop={true} component={ChatPage} componentProps={{ view: 'list' }} />
              }
            />
            <Route
              path="/chat/:conversationId"
              element={
                <MyRoute isRestricted={true} needShop={true} component={ChatPage} componentProps={{ view: 'chat' }} />
              }
            />
            <Route
              path="/chat/:conversationId/crm"
              element={
                <MyRoute isRestricted={true} needShop={true} component={ChatPage} componentProps={{ view: 'crm' }} />
              }
            />
          </Routes>
        </Suspense>
      </AppShell>
    );
  };

  return (
    <Provider store={Store}>
      <ThemeProvider>
        <SocketContext.Provider value={socket}>
          <Router>
            <Alert />
            <Toaster />
            {showContent()}
          </Router>
        </SocketContext.Provider>
      </ThemeProvider>
    </Provider>
  );
};

export default App;
